import type { Dispatch, SetStateAction } from "react";
import { useAdminRequestScope } from "./useAdminRequestScope";
import { useCallback, useEffect, useRef, useState } from "react";
import { apiRequest } from "../../shared/api/client";
import type { Notify, DepartmentRow } from "./admin-types";

export function useAdminDepartmentsWorkspace({notify}: {notify: Notify}) {
  const scope = useAdminRequestScope(notify);
  const [rows, setRows] = useState<DepartmentRow[]>([]);
  const [editing, setEditingState] = useState<DepartmentRow | null>(null);
  const [composerOpen, setComposerOpenState] = useState(false);
  const [form, setFormState] = useState({ code: "", name: "", active: true });
  const pending = useRef(false);
  const revision = useRef(0);
  const renderedRevision = revision.current;
  const [saving, setSaving] = useState(false);
  const setForm: Dispatch<SetStateAction<typeof form>> = value => {revision.current++; setFormState(value);};
  const setComposerOpen: Dispatch<SetStateAction<boolean>> = value => {revision.current++; setComposerOpenState(value);};
  const setEditing: Dispatch<SetStateAction<DepartmentRow | null>> = value => {revision.current++; setEditingState(value);};
  const load = useCallback(() => {
    if (!scope.isMounted()) return;
    const request = scope.beginRead();
    void apiRequest<DepartmentRow[]>("/api/admin/departments").then(value => {
      if (scope.isCurrentRead(request)) setRows(value);
    }).catch((error: unknown) => {
      if (scope.isCurrentRead(request)) scope.notify(error instanceof Error ? error.message : "Departmanlar alınamadı.");
    });
  }, [scope]);
  useEffect(load, [load]);
  const save = async () => {
    if (!scope.isMounted() || pending.current || renderedRevision !== revision.current) return;
    if (!form.name.trim() || (!editing && !form.code.trim())) {
      scope.notify("Departman adı ve kodu zorunludur.");
      return;
    }
    pending.current = true;
    setSaving(true);
    const submittedRevision = revision.current;
    try {
      const saved = await apiRequest<DepartmentRow>(
        editing
          ? `/api/admin/departments/${editing.id}`
          : "/api/admin/departments",
        {
          method: editing ? "PUT" : "POST",
          body: JSON.stringify({
            ...form,
            code: form.code.trim().toUpperCase().replaceAll(" ", "_"),
          }),
        },
      );
      if (!scope.isMounted()) return;
      scope.invalidateReads();
      setRows((current) =>
        editing
          ? current.map((row) => (row.id === saved.id ? saved : row))
          : [...current, saved],
      );
      if (submittedRevision === revision.current) {
        revision.current++;
        setEditingState(null);
        setComposerOpenState(false);
        setFormState({ code: "", name: "", active: true });
      }
      scope.notify(`Departman ${editing ? "güncellendi" : "oluşturuldu"}.`);
    } catch (error) {
      scope.notify(
        error instanceof Error ? error.message : "Departman kaydedilemedi.",
      );
    } finally {
      pending.current = false;
      if (scope.isMounted()) setSaving(false);
    }
  };
  const edit = (row: DepartmentRow) => {
    setEditing(row);
    setComposerOpen(true);
    setForm({ code: row.code, name: row.name, active: row.active });
  };
  return { saving, rows, editing, setEditing, composerOpen, setComposerOpen, form, setForm, save, edit };
}
